// SuperAdminPromptRegistry — Jun 2026 · PIPELINE_SIMPLIFICATION_PROJECT.md item E
//
// Randy: "I almost feel like it would be nice if we built a tab in the super
// admin that gives a home for all these rules... clearly grouped under what and
// where they impact. Are they for the whole engine, or which door do they
// impact. this would help me see, if im over engineering, creating bloat, or
// context confusion etc."
//
// Read-only, deliberately. The page calls the SAME functions /ask calls, so it
// can never drift from the engine. It cannot edit a universal rule, because a
// prompt that reads its rules from a table makes "why did this silo answer
// differently" unanswerable, which is the opposite of the point.
import { useCallback, useEffect, useState } from "react";
import {
  Scales, Spinner, Warning, CheckCircle, Info, CaretRight, CaretDown,
  Gear, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const GROUP_META = {
  Universal: { tint: "#3A4A3E", note: "Every door. Changing these changes every answer on the platform." },
  Voice: { tint: "#7A5C3E", note: "Every door, but owned by this creator's own material." },
  Evidence: { tint: "#2F6B3A", note: "The creator's content. Not instruction." },
  Door: { tint: "#C16E5A", note: "Only the door shown above." },
};

const SEV = {
  error: { icon: Warning, tint: "#B4432F", label: "Conflict" },
  warn: { icon: Warning, tint: "#A8741A", label: "Worth a look" },
  info: { icon: Info, tint: "#3A5A7A", label: "By design" },
  ok: { icon: CheckCircle, tint: "#2F6B3A", label: "Clear" },
};

function Bar({ instr, evid }) {
  const total = Math.max(1, instr + evid);
  const pct = (100 * instr) / total;
  return (
    <div data-testid="registry-budget-bar">
      <div className="flex h-7 w-full overflow-hidden rounded-md border hairline">
        <div
          className="flex items-center justify-center bg-[#C16E5A] text-[11px] font-medium text-white"
          style={{ width: `${pct}%` }}
        >
          {pct >= 18 ? `${pct.toFixed(1)}% rules` : ""}
        </div>
        <div
          className="flex items-center justify-center bg-[#3A4A3E] text-[11px] font-medium text-white"
          style={{ width: `${100 - pct}%` }}
        >
          {100 - pct >= 18 ? `${(100 - pct).toFixed(1)}% your content` : ""}
        </div>
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-[#6B6A68]">
        <span>{instr.toLocaleString()} chars of instruction</span>
        <span>{evid.toLocaleString()} chars of evidence</span>
      </div>
    </div>
  );
}

function BlockRow({ b }) {
  const [open, setOpen] = useState(false);
  const meta = GROUP_META[b.group] || GROUP_META.Door;
  return (
    <div className="border-b hairline last:border-b-0" data-testid={`registry-block-${b.name.replace(/\W+/g, "-").toLowerCase()}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[#F7F7F4]"
      >
        {open ? <CaretDown size={13} className="mt-1 shrink-0 text-[#6B6A68]" />
              : <CaretRight size={13} className="mt-1 shrink-0 text-[#6B6A68]" />}
        <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ background: meta.tint }} />
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-[#1C1B1A]">{b.name}</span>
          <span className="mt-0.5 block text-[11px] leading-relaxed text-[#6B6A68]">
            {b.where} · {b.enforcement}
            {b.note ? <> · {b.note}</> : null}
          </span>
        </span>
        <span className="shrink-0 text-right">
          <span className="block text-sm tabular-nums text-[#1C1B1A]">{b.chars.toLocaleString()}</span>
          <span className="block text-[10px] uppercase tracking-wider text-[#6B6A68]">~{b.tokens} tok</span>
        </span>
      </button>
      {open && (
        <pre
          data-testid="registry-block-text"
          className="mx-3 mb-3 max-h-80 overflow-auto whitespace-pre-wrap rounded-md border hairline bg-[#FBFAF6] p-3 text-[11px] leading-relaxed text-[#2A2927]"
        >
          {b.text || "(empty on this silo)"}
        </pre>
      )}
    </div>
  );
}

export default function SuperAdminPromptRegistry() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [siloId, setSiloId] = useState("");
  const [door, setDoor] = useState("public");
  const [question, setQuestion] = useState("how do i get my heads lights to work?");

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/prompt-registry", {
        params: {
          silo_id: opts.siloId ?? siloId ?? undefined,
          door: opts.door ?? door,
          question: opts.question ?? question,
        },
      });
      setData(r.data);
      if (!siloId && r.data?.silo?.id) setSiloId(r.data.silo.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the prompt registry.");
    } finally {
      setLoading(false);
    }
  }, [siloId, door, question]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const groups = ["Universal", "Voice", "Evidence", "Door"];

  return (
    <div className="space-y-5" data-testid="admin-prompt-registry">
      <div className="rounded-lg border hairline bg-[#FDFCF8] p-4">
        <div className="flex items-start gap-2.5">
          <Scales size={18} weight="duotone" className="mt-0.5 shrink-0 text-[#3A4A3E]" />
          <div className="min-w-0">
            <h2 className="text-base text-[#1C1B1A]">Prompt &amp; Rules Registry</h2>
            <p className="mt-1 text-xs leading-relaxed text-[#6B6A68]">
              Everything that reaches the model on one ask, assembled by the same code that
              serves it. Read-only on purpose: a prompt that read its rules from a table would
              let every silo drift, and then nobody could answer why two silos replied
              differently. Pick a silo, a door and a question, and see the whole stack.
            </p>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-xs text-[#6B6A68]">Silo</span>
            <select
              data-testid="registry-silo-select"
              value={siloId}
              onChange={(e) => { setSiloId(e.target.value); load({ siloId: e.target.value }); }}
              className="w-full rounded-md border hairline bg-white px-3 py-2 text-sm"
            >
              {(data?.silos || []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-[#6B6A68]">Door</span>
            <select
              data-testid="registry-door-select"
              value={door}
              onChange={(e) => { setDoor(e.target.value); load({ door: e.target.value }); }}
              className="w-full rounded-md border hairline bg-white px-3 py-2 text-sm"
            >
              {(data?.doors || []).map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-[#6B6A68]">Question</span>
            <div className="flex gap-2">
              <input
                data-testid="registry-question-input"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                className="min-w-0 flex-1 rounded-md border hairline bg-white px-3 py-2 text-sm"
              />
              <button
                type="button"
                data-testid="registry-reload-btn"
                onClick={() => load()}
                disabled={loading}
                className="shrink-0 rounded-md border hairline bg-white px-3 text-[#3A4A3E] hover:border-[#4A5D4E] disabled:opacity-50"
                aria-label="Reassemble the prompt"
              >
                {loading ? <Spinner size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
              </button>
            </div>
          </label>
        </div>

        {data?.doors && (
          <p className="mt-2.5 text-[11px] leading-relaxed text-[#6B6A68]" data-testid="registry-door-note">
            {data.doors.find((d) => d.id === door)?.note}
          </p>
        )}
      </div>

      {!data && loading && (
        <div className="flex items-center gap-2 px-1 text-sm text-[#6B6A68]">
          <Spinner size={14} className="animate-spin" /> Assembling…
        </div>
      )}

      {data && (
        <>
          <div className="rounded-lg border hairline bg-[#FDFCF8] p-4" data-testid="registry-budget">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm text-[#1C1B1A]">What the model is reading</h3>
              <span className="text-[11px] text-[#6B6A68]">
                {data.silo.name} · voice: {data.silo.voice_mode} · tier 1: {data.silo.tier1_mode}
                {data.silo.is_youtube_twin ? " · YouTube twin" : ""}
              </span>
            </div>
            <Bar instr={data.budget.instruction_chars} evid={data.budget.evidence_chars} />
            <p className="mt-2 text-[11px] leading-relaxed text-[#6B6A68]">
              Retrieved for this question: {data.tiers.tier1_chunks} Tier 1 ·{" "}
              {data.tiers.tier2_chunks} Tier 2 · {data.tiers.tier3_chunks} Tier 3 passages.
              Roughly {(data.budget.instruction_tokens + data.budget.evidence_tokens).toLocaleString()} input
              tokens before the question itself.
            </p>
          </div>

          <div className="rounded-lg border hairline bg-[#FDFCF8] p-4" data-testid="registry-conflicts">
            <h3 className="mb-2 text-sm text-[#1C1B1A]">Conflict report</h3>
            <div className="space-y-2">
              {data.conflicts.map((c, i) => {
                const m = SEV[c.severity] || SEV.info;
                const Icon = m.icon;
                return (
                  <div
                    key={i}
                    data-testid={`registry-conflict-${c.severity}`}
                    className="flex items-start gap-2.5 rounded-md border hairline bg-white px-3 py-2.5"
                  >
                    <Icon size={15} weight="duotone" className="mt-0.5 shrink-0" style={{ color: m.tint }} />
                    <div className="min-w-0">
                      <div className="text-sm text-[#1C1B1A]">
                        <span
                          className="mr-2 text-[10px] uppercase tracking-wider"
                          style={{ color: m.tint }}
                        >
                          {m.label}
                        </span>
                        {c.title}
                      </div>
                      <p className="mt-0.5 text-[11px] leading-relaxed text-[#6B6A68]">{c.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {groups.map((g) => {
            const rows = data.blocks.filter((b) => b.group === g);
            if (!rows.length) return null;
            const sum = rows.reduce((a, b) => a + b.chars, 0);
            return (
              <div key={g} className="rounded-lg border hairline bg-[#FDFCF8]" data-testid={`registry-group-${g.toLowerCase()}`}>
                <div className="flex items-start justify-between gap-3 border-b hairline px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm text-[#1C1B1A]">
                      <span className="h-2 w-2 rounded-full" style={{ background: GROUP_META[g].tint }} />
                      {g}
                    </div>
                    <p className="mt-0.5 text-[11px] text-[#6B6A68]">{GROUP_META[g].note}</p>
                  </div>
                  <span className="shrink-0 text-sm tabular-nums text-[#6B6A68]">
                    {sum.toLocaleString()}
                  </span>
                </div>
                {rows.map((b) => <BlockRow key={b.name} b={b} />)}
              </div>
            );
          })}

          <div className="rounded-lg border hairline bg-[#FDFCF8] p-4" data-testid="registry-mechanisms">
            <div className="mb-1 flex items-center gap-2">
              <Gear size={15} weight="duotone" className="text-[#3A4A3E]" />
              <h3 className="text-sm text-[#1C1B1A]">Code that shapes the answer after it is written</h3>
            </div>
            <p className="mb-3 text-[11px] leading-relaxed text-[#6B6A68]">
              None of this appears in the prompt, which is exactly why it belongs here. The ones
              marked <strong>enforced</strong> have no off switch on purpose: a kill switch on the
              URL scrub, for instance, would let an invented link reach a reader.
            </p>
            <div className="space-y-1.5">
              {data.mechanisms.map((m) => (
                <div
                  key={m.name}
                  data-testid={`registry-mechanism-${m.name.replace(/\W+/g, "-").toLowerCase()}`}
                  className="flex items-start gap-3 rounded-md border hairline bg-white px-3 py-2"
                >
                  <span
                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                    style={{ background: m.state === "on" ? "#2F6B3A" : "#B9B7B0" }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-sm text-[#1C1B1A]">{m.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-[#6B6A68]">
                        {m.state} · {m.optional ? "optional" : "enforced"}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11px] leading-relaxed text-[#6B6A68]">
                      {m.where} · {(m.doors || []).join(", ")}
                    </p>
                    <p className="mt-0.5 text-[11px] leading-relaxed text-[#6B6A68]">{m.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
